import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Calendar, CalendarClock, Wrench, Users, Clock, CalendarX,
  Contact, Plug, Settings as SettingsIcon, Menu, LogOut, Share2, CalendarPlus, ShieldCheck,
  FileText,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { toast } from "@/components/ui/sonner";
import NewBookingDialog from "@/components/NewBookingDialog";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/calendar", label: "Calendrier", icon: Calendar, testid: "nav-calendar" },
  { to: "/appointments", label: "Rendez-vous", icon: CalendarClock, testid: "nav-appointments" },
  { to: "/quotes", label: "Devis", icon: FileText, testid: "nav-quotes" },
  { to: "/services", label: "Services", icon: Wrench, testid: "nav-services" },
  { to: "/employees", label: "Employés", icon: Users, testid: "nav-employees" },
  { to: "/availability", label: "Disponibilités", icon: Clock, testid: "nav-availability" },
  { to: "/exceptions", label: "Exceptions", icon: CalendarX, testid: "nav-exceptions" },
  { to: "/customers", label: "Clients", icon: Contact, testid: "nav-clients" },
  { to: "/integrations", label: "Intégrations", icon: Plug, testid: "nav-integrations" },
  { to: "/settings", label: "Paramètres", icon: SettingsIcon, testid: "nav-settings" },
];

function NavItems({ onNavigate }) {
  return (
    <nav className="flex flex-col gap-1 px-3">
      {NAV.map(({ to, label, icon: Icon, testid, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onNavigate}
          data-testid={testid}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive ? "bg-blue-600 text-white" : "text-slate-400 hover:bg-slate-800 hover:text-slate-100"
            }`
          }
        >
          <Icon className="h-4 w-4 shrink-0" />
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function DashboardLayout() {
  const { user, tenant, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);

  const bookingUrl = `${window.location.origin}/b/${tenant.slug}`;
  const quoteUrl = `${window.location.origin}/quote/${tenant.slug}`;

  const copyBookingLink = async () => {
    try {
      await navigator.clipboard.writeText(bookingUrl);
      toast.success("Lien de réservation copié !");
    } catch {
      toast.info(bookingUrl);
    }
  };

  const copyQuoteLink = async () => {
    try {
      await navigator.clipboard.writeText(quoteUrl);
      toast.success("Lien de demande de devis copié !");
    } catch {
      toast.info(quoteUrl);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col bg-slate-900 lg:flex" data-testid="sidebar">
        <div className="flex h-16 items-center gap-2 px-6">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
            <CalendarClock className="h-4 w-4 text-white" />
          </div>
          <span className="font-display text-lg font-bold text-white">RendezVous</span>
        </div>
        <div className="px-6 pb-4">
          <p className="text-xs font-medium uppercase tracking-wider text-slate-500" data-testid="sidebar-tenant-name">{tenant.name}</p>
        </div>
        <div className="flex-1 overflow-y-auto">
          <NavItems />
        </div>
        <div className="border-t border-slate-800 p-4">
          {user?.is_platform_admin && (
            <button
              onClick={() => navigate("/admin")}
              data-testid="nav-platform-admin"
              className="mb-2 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-400 hover:bg-slate-800 hover:text-slate-100"
            >
              <ShieldCheck className="h-4 w-4" /> Admin plateforme
            </button>
          )}
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-slate-200">{user?.name}</p>
              <p className="truncate text-xs text-slate-500">{user?.email}</p>
            </div>
            <button onClick={handleLogout} data-testid="btn-logout" className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white" title="Se déconnecter">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-2 border-b border-slate-200 bg-white px-4 sm:px-6">
          <div className="flex items-center gap-2">
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="btn-mobile-menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 bg-slate-900 p-0 pt-6 border-slate-800">
                <p className="px-6 pb-4 text-xs font-medium uppercase tracking-wider text-slate-500">{tenant.name}</p>
                <NavItems onNavigate={() => setMenuOpen(false)} />
              </SheetContent>
            </Sheet>
            <h1 className="font-display text-base font-semibold text-slate-900 sm:text-lg">{tenant.name}</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={copyBookingLink} className="sm:hidden" data-testid="btn-share-sms-link-mobile">
              <Share2 className="h-4 w-4" />
            </Button>
            <Button size="sm" onClick={() => setBookingOpen(true)} data-testid="btn-new-appointment">
              <CalendarPlus className="mr-2 h-4 w-4" /> <span className="hidden sm:inline">Nouveau RDV</span><span className="sm:hidden">RDV</span>
            </Button>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      <NewBookingDialog open={bookingOpen} onOpenChange={setBookingOpen} onCreated={() => window.dispatchEvent(new Event("bookings-changed"))} />
    </div>
  );
}